import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  Assignment,
  Person,
  Psychology,
  Description,
  Download,
} from '@mui/icons-material';
import {
  providerAssessmentAPI,
  PatientAssessment,
  AssessmentSummary,
  assessmentUtils,
} from '../../services/providerAssessmentService';
import PatientAssessmentSummary from './PatientAssessmentSummary';
import BiopsychosocialReview from './BiopsychosocialReview';
import FMLAFormGenerator from './FMLAFormGenerator';

interface AssessmentReviewTabProps {
  appointmentId: string;
  patientEmail: string;
}

const AssessmentReviewTab: React.FC<AssessmentReviewTabProps> = ({
  appointmentId,
  patientEmail,
}) => {
  const [assessments, setAssessments] = useState<PatientAssessment[]>([]);
  const [summary, setSummary] = useState<AssessmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string>('summary');

  useEffect(() => {
    loadAssessmentData();
  }, [appointmentId]);

  const loadAssessmentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load both assessments and summary in parallel
      const [assessmentsResponse, summaryResponse] = await Promise.all([
        providerAssessmentAPI.getPatientAssessments(appointmentId),
        providerAssessmentAPI.getAssessmentSummary(appointmentId),
      ]);

      setAssessments(assessmentsResponse.data);
      setSummary(summaryResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load patient assessments');
    } finally {
      setLoading(false);
    }
  };

  const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : '');
  };

  const completionSummary = assessmentUtils.getCompletionSummary(assessments);
  const fmlaEligible = assessmentUtils.calculateFMLAEligibility(assessments);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        <Button onClick={loadAssessmentData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card sx={{ textAlign: 'center', py: 6 }}>
        <CardContent>
          <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Assessments Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Patient has not completed any assessments for this appointment.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Patient Assessment Review
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Patient: {patientEmail}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${completionSummary.completed}/${completionSummary.total} Completed`}
            color={completionSummary.percentage === 100 ? 'success' : 'warning'}
            icon={<Assignment />}
          />
          <Chip 
            label={fmlaEligible ? 'FMLA Eligible' : 'Review Required'}
            color={fmlaEligible ? 'success' : 'info'}
            icon={<Person />}
          />
        </Box>
      </Box>

      {/* Assessment Summary */}
      <Accordion 
        expanded={expandedPanel === 'summary'} 
        onChange={handlePanelChange('summary')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology color="primary" />
            <Typography variant="h6">Clinical Summary</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {summary && <PatientAssessmentSummary summary={summary} assessments={assessments} />}
        </AccordionDetails>
      </Accordion>

      {/* PHQ-9 Assessment */}
      {assessmentUtils.getAssessmentByType(assessments, 'phq9') && (
        <Accordion 
          expanded={expandedPanel === 'phq9'} 
          onChange={handlePanelChange('phq9')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment color="info" />
              <Typography variant="h6">PHQ-9 Depression Scale</Typography>
              {assessmentUtils.getAssessmentByType(assessments, 'phq9')?.scoreLevel && (
                <Chip 
                  label={assessmentUtils.formatScoreLevel(
                    assessmentUtils.getAssessmentByType(assessments, 'phq9')?.scoreLevel
                  )}
                  size="small"
                  color={assessmentUtils.getSeverityColor(
                    assessmentUtils.getAssessmentByType(assessments, 'phq9')?.scoreLevel
                  )}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PatientAssessmentSummary 
              summary={summary} 
              assessments={assessments}
              focusTemplate="phq9" 
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* GAD-7 Assessment */}
      {assessmentUtils.getAssessmentByType(assessments, 'gad7') && (
        <Accordion 
          expanded={expandedPanel === 'gad7'} 
          onChange={handlePanelChange('gad7')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Psychology color="secondary" />
              <Typography variant="h6">GAD-7 Anxiety Scale</Typography>
              {assessmentUtils.getAssessmentByType(assessments, 'gad7')?.scoreLevel && (
                <Chip 
                  label={assessmentUtils.formatScoreLevel(
                    assessmentUtils.getAssessmentByType(assessments, 'gad7')?.scoreLevel
                  )}
                  size="small"
                  color={assessmentUtils.getSeverityColor(
                    assessmentUtils.getAssessmentByType(assessments, 'gad7')?.scoreLevel
                  )}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <PatientAssessmentSummary 
              summary={summary} 
              assessments={assessments}
              focusTemplate="gad7" 
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Biopsychosocial Assessment */}
      {assessmentUtils.getAssessmentByType(assessments, 'biopsychosocial') && (
        <Accordion 
          expanded={expandedPanel === 'biopsychosocial'} 
          onChange={handlePanelChange('biopsychosocial')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="warning" />
              <Typography variant="h6">Biopsychosocial Assessment</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <BiopsychosocialReview 
              assessment={assessmentUtils.getAssessmentByType(assessments, 'biopsychosocial')!}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Document Review */}
      {assessments.some(a => a.uploadedDocuments && a.uploadedDocuments.length > 0) && (
        <Accordion 
          expanded={expandedPanel === 'documents'} 
          onChange={handlePanelChange('documents')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description color="success" />
              <Typography variant="h6">Supporting Documents</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {assessments
                .filter(a => a.uploadedDocuments && a.uploadedDocuments.length > 0)
                .map(assessment => 
                  assessment.uploadedDocuments?.map(doc => (
                    <Grid item xs={12} sm={6} key={doc.documentId}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {doc.filename}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Uploaded: {new Date(doc.uploadedAt).toLocaleString()}
                          </Typography>
                          <Button 
                            size="small" 
                            startIcon={<Download />}
                            onClick={async () => {
                              try {
                                
                                // Get secure download URL from backend
                                const response = await providerAssessmentAPI.getDocumentDownloadUrl(appointmentId, doc.documentId);
                                const downloadUrl = response.data.downloadUrl;
                                
                                // Create temporary link and trigger download
                                const link = document.createElement('a');
                                link.href = downloadUrl;
                                link.download = doc.fileName || `document-${doc.documentId}`;
                                link.style.display = 'none';
                                
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                
                              } catch (error) {
                                // Could add toast notification here for user feedback
                              }
                            }}
                          >
                            Download
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))
                )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      <Divider sx={{ my: 3 }} />

      {/* FMLA Form Generation */}
      <FMLAFormGenerator 
        appointmentId={appointmentId}
        assessments={assessments}
        summary={summary}
        eligible={fmlaEligible}
      />
    </Box>
  );
};

export default AssessmentReviewTab;